import { sendAppEvent } from "./Utils";
import { TourIds, ValidTourVariations } from "./components/StickyTour/TourTypes";
import type { ImportFeedbackCohort } from "~shared/importFeedback";

export enum StoryBibleAction {
  Rewrite = "rewrite",
  AddedChapter = "added chapter",
  AddedAct = "added act",
  RestoreHistory = "restore history",
  CreateChapterForOutline = "create chapter for outline",
  Export = "export",
  Delete = "delete",
  Import = "import",
  SelectOutlineType = "select outline type",
  SetCustomOutlineInstructions = "set custom outline instructions",
  ToggleCollapse = "toggle collapse",
  NavigateToLinkedDocument = "navigate to linked document",
  AddBlankElement = "add blank element",
  Reorder = "reorder",
  CustomizeDefaults = "customize defaults",
  RoleChange = "role change",
  ToggleVisibility = "toggle visibility",
  AddCustomField = "add custom field",
  OpenHistory = "open history",
  AddBlankCharacter = "add blank character",
  CreateCustomDefaults = "create custom defaults",
  SelectedFeaturedStyle = "selected featured style",
  SelectedCustomStyle = "selected custom style",
  OpenMatchMyStyle = "open match my style",
  PasteMyStyle = "paste my style",
  SubmitStyleExample = "submit style example",
  SelectMyStyle = "select my style",
  SendStyleToDoc = "send style to doc",
  EnteredTextFromBlank = "entered text from blank",
  OpenCharacterGenerate = "open character generate",
  OpenWorldbuildingGenerate = "open worldbuilding generate",
  CancelStream = "cancel stream",
  CopyAllCharacters = "copy all characters",
  CopyCharacter = "copy single character",
  CopyAllWorldbuilding = "copy all worldbuilding",
  CopyWorldbuilding = "copy single worldbuilding",
}

export enum StoryBibleType {
  braindump = "seed",
  genre = "genre",
  style = "style",
  synopsis = "synopsis",
  characters = "characters",
  worldbuilding = "worldbuilding",
  outline = "outline",
}

export enum StoryBibleGenerateType {
  CharacterCast = "character cast",
  OutlineChapter = "outline chapter",
  RewriteOutlineChapter = "rewrite outline chapter",
  UpdateOutlineChapterFromDocument = "update outline chapter from document",
  TraitRegeneration = "trait regeneration",
}

export enum AgenticChatMode {
  Write = "write",
  Chat = "chat",
}

export enum AgenticChatFeedback {
  Like = "like",
  Dislike = "dislike",
  Unlike = "unlike",
  Undislike = "undislike",
}

export enum AgenticChatMentionType {
  Character = "character",
  Worldbuilding = "worldbuilding",
  Document = "document",
  Outline = "outline",
}

type ImportFeedbackTrackingProps = {
  projectId: string;
  invocationId: string;
  variation: "Classic" | "Agentic";
  cohort?: ImportFeedbackCohort;
};

export type FeedbackPlanUpsellSource = "toolbar_button" | "toolbar_chevron";

type FeedbackPlanUpsellProps = {
  feedbackUpsellSource?: FeedbackPlanUpsellSource;
  currentPlanTitle?: string | null;
};

export type EntityImageEntrySource = "cover_click_empty" | "cover_click_existing" | "add_click" | "alternate_click" | "overflow_click";
export type EntityImageTab = "gallery" | "generate" | "upload" | "viewer";

export const AppEventPublisher = {
  meta: {
    loginModalViewed: () => {
      void sendAppEvent("Login Modal Viewed");
    },
    loginWithApplePressed: () => {
      void sendAppEvent("Login with Apple Pressed");
    },
    demo: {
      start: () => {
        void sendAppEvent("Start Demo - New User");
      },
      step: (step: string) => {
        void sendAppEvent("Demo - Step id: " + step);
      },
    },
  },
  projects: {
    viewProjectHome: () => {
      void sendAppEvent("Projects - View Project Home");
    },
    createProject: (props: { uiSource?: string } = {}) => {
      void sendAppEvent("Projects - Create Project", props);
    },
    generateProject: (props: { uiSource?: string } = {}) => {
      void sendAppEvent("Projects - Generate Project", props);
    },
    importProject: (props: { uiSource?: string } = {}) => {
      void sendAppEvent("Projects - Import", props);
    },
    createFolder: (props: { uiSource?: string } = {}) => {
      void sendAppEvent("Projects - Create Folder", props);
    },
    createSeries: (props: { uiSource?: string } = {}) => {
      void sendAppEvent("Projects - Create Series", props);
    },
    deleteFolder: () => {
      void sendAppEvent("Projects - Delete Folder");
    },
    moveFolder: () => {
      void sendAppEvent("Projects - Move Folder");
    },
    openProject: (createdFrom: "demo" | "empty-signup" | "import-wip" | "sudowrite-app" | string) => {
      // There may be other sources that I don't know about
      void sendAppEvent("Projects - Open Project", { createdFrom });
    },
  },
  document: {},
  plugins: {},
  feedback: {
    planUpsellTriggered: (props: FeedbackPlanUpsellProps) => {
      void sendAppEvent("Feedback - Plan Upsell Triggered", props);
    },
    planUpsellViewed: (props: FeedbackPlanUpsellProps) => {
      void sendAppEvent("Feedback - Plan Upsell Viewed", props);
    },
    planUpsellLearnMoreClicked: (props: FeedbackPlanUpsellProps) => {
      void sendAppEvent("Feedback - Plan Upsell Learn More Clicked", props);
    },
    planUpsellUpgradeClicked: (props: FeedbackPlanUpsellProps) => {
      void sendAppEvent("Feedback - Plan Upsell Upgrade Clicked", props);
    },
  },
  onboarding: {
    startTour: (tourId: TourIds, variation?: ValidTourVariations) => {
      void sendAppEvent("Onboarding - Start Tour", { tourId, variation });
    },
    stepCompleted: (tourId: TourIds, stepId: string, stepIndex: number, variation?: ValidTourVariations) => {
      void sendAppEvent("Onboarding - Step Completed", { tourId, stepId, stepIndex, variation });
    },
    completeTour: (tourId: TourIds, variation?: ValidTourVariations) => {
      void sendAppEvent("Onboarding - Complete Tour", { tourId, variation });
    },
    exitTour: (tourId: TourIds, stepId: string, stepIndex: number, variation?: ValidTourVariations) => {
      void sendAppEvent("Onboarding - Exit Tour", { tourId, stepId, stepIndex, variation });
    },
    oldSurveyModalViewed: () => {
      void sendAppEvent("Onboarding - Old Survey Modal Viewed");
    },
    shownStoryBibleNextFromWriteRewrite: () => {
      void sendAppEvent("Onboarding - Shown Story Bible Next From Write-Rewrite");
    },
    acceptedStoryBibleTourFromWriteRewrite: () => {
      void sendAppEvent("Onboarding - Accepted Story Bible Tour From Write-Rewrite");
    },
    declinedStoryBibleTourFromWriteRewrite: () => {
      void sendAppEvent("Onboarding - Declined Story Bible Tour From Write-Rewrite");
    },
    shownWriteRewriteNextFromPlugins: () => {
      void sendAppEvent("Onboarding - Shown Write-Rewrite Next From Plugins");
    },
    acceptedWriteRewriteTourFromPlugins: () => {
      void sendAppEvent("Onboarding - Accepted Write-Rewrite Tour From Plugins");
    },
    declinedWriteRewriteTourFromPlugins: () => {
      void sendAppEvent("Onboarding - Declined Write-Rewrite Tour From Plugins");
    },
    pluginInstalled: (pluginName: string) => {
      void sendAppEvent("Onboarding - Plugin Installed", { pluginName });
    },
    pluginUninstalled: (pluginName: string) => {
      void sendAppEvent("Onboarding - Plugin Uninstalled", { pluginName });
    },
  },
  credits: {
    outOfCredits: (isBonus: boolean, tourContext?: { tourId: TourIds; stepId: string; stepIndex: number; variation?: ValidTourVariations }) => {
      void sendAppEvent("Credits - Out of Credits", { isBonus, ...tourContext });
    },
    claimedBonus: (amount: number, tourContext?: { tourId: TourIds; stepId: string; stepIndex: number; variation?: ValidTourVariations }) => {
      void sendAppEvent("Credits - Claimed Bonus", { amount, ...tourContext });
    },
  },
  storyBible: {
    generate: (type: StoryBibleType | StoryBibleGenerateType) => {
      void sendAppEvent("Story Bible Generate", { type });
    },
    edited: (type: StoryBibleType, action: StoryBibleAction) => {
      void sendAppEvent("Story Bible Edited", { type, action });
    },
    copy: (type: StoryBibleType, action: "button" | "select") => {
      void sendAppEvent("Story Bible Copy", { type, action });
    },
    action: (type: StoryBibleType, action: StoryBibleAction) => {
      void sendAppEvent("Story Bible Action", { type, action });
    },
    toggleCollapse: (itemType: "outline chapter" | "worldbuilding" | "character", action: StoryBibleAction) => {
      void sendAppEvent("Story Bible Action", { itemType, action });
    },
    customizeDefaults: (type: StoryBibleType) => {
      void sendAppEvent("Story Bible Action", { type, action: StoryBibleAction.CustomizeDefaults });
    },
    toggleVisibility: (type: StoryBibleType) => {
      void sendAppEvent("Story Bible Action", { type, action: StoryBibleAction.ToggleVisibility });
    },
    style: {
      selectedFeaturedStyle: (style: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.style, action: StoryBibleAction.SelectedFeaturedStyle, style });
      },
      openMatchMyStyle: () => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.style, action: StoryBibleAction.OpenMatchMyStyle });
      },
    },
    characters: {
      roleChange: (selectedRole: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.characters, action: StoryBibleAction.RoleChange, selectedRole });
      },
      generateTrait: (generateFromBlank: boolean) => {
        void sendAppEvent("Story Bible Generate", { type: StoryBibleGenerateType.TraitRegeneration, target: "characters", generateFromBlank });
      },
      openGenerate: (position: "top" | "bottom") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.characters, action: StoryBibleAction.OpenCharacterGenerate, position });
      },
      addBlankCharacter: (position: "top" | "bottom") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.characters, action: StoryBibleAction.AddBlankCharacter, position });
      },
      copyAllCharacters: () => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.characters, action: StoryBibleAction.CopyAllCharacters });
      },
      copyCharacter: () => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.characters, action: StoryBibleAction.CopyCharacter });
      },
    },
    worldbuilding: {
      addBlankElement: (elementType: string, position: "top" | "bottom") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.worldbuilding, action: StoryBibleAction.AddBlankElement, elementType, position });
      },
      roleChange: (selectedRole: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.worldbuilding, action: StoryBibleAction.RoleChange, selectedRole });
      },
      generateTrait: (generateFromBlank: boolean) => {
        void sendAppEvent("Story Bible Generate", { type: StoryBibleGenerateType.TraitRegeneration, target: "worldbuilding", generateFromBlank });
      },
      openGenerate: (position: "top" | "bottom") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.worldbuilding, action: StoryBibleAction.OpenWorldbuildingGenerate, position });
      },
      copyAllWorldbuilding: () => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.worldbuilding, action: StoryBibleAction.CopyAllWorldbuilding });
      },
      copyWorldbuilding: () => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.worldbuilding, action: StoryBibleAction.CopyWorldbuilding });
      },
    },
    outline: {
      addChapter: (position: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.outline, action: StoryBibleAction.AddedChapter, position });
      },
      addAct: (position: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.outline, action: StoryBibleAction.AddedAct, position });
      },
      addChapterDocument: (origin: "under-outline" | "menu" | "under-document" | "left-bar" | "chapter-header") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.outline, action: StoryBibleAction.CreateChapterForOutline, origin });
      },
      delete: (itemType: "chapter" | "act" | "entire-outline") => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.outline, action: StoryBibleAction.Delete, itemType });
      },
      selectOutlineType: (outlineType: string) => {
        void sendAppEvent("Story Bible Action", { type: StoryBibleType.outline, action: StoryBibleAction.SelectOutlineType, outlineType });
      },
    },
    draft: {
      cancelPreflight: () => {
        void sendAppEvent("Draft Cancel Preflight");
      },
      cancelProseGeneration: () => {
        void sendAppEvent("Draft Cancel Prose Generation");
      },
      pauseProseGeneration: (props: {
        invocationId?: string | null;
        timestamp?: string;
        elapsedTimeMs?: number;
        deliveredProseWordCount?: number | null;
      } = {}) => {
        void sendAppEvent("Draft Pause Prose Generation", props, { retries: props.invocationId ? 2 : 0 });
      },
      generateProse: (engine: string, isContinue: boolean) => {
        void sendAppEvent("Draft Generate Prose", { engine, isContinue });
      },
      rewriteScenes: (scenesEngine: string) => {
        void sendAppEvent("Draft Rewrite Scenes", { scenesEngine });
      },
      preflightProse: (engine: string, isContinue: boolean) => {
        void sendAppEvent("Draft Preflight Prose", { engine, isContinue });
      },
      preflightLocalCheck: (condition: string) => {
        void sendAppEvent("Draft Preflight Local Check Match", { condition });
      },
      preflightCheckResult: (type: "Approval" | "Suggestion" | "Error") => {
        void sendAppEvent("Draft Preflight Check Result", { type });
      },
    },
  },
  agenticChat: {
    messageSent: (props: {
      mode: AgenticChatMode;
      model: string;
      chatLength: number;
      hasAttachments: boolean;
      hasMentions: boolean;
      hasPassage: boolean;
      isNewChat: boolean;
    }) => {
      void sendAppEvent("Agentic Chat - Message Sent", props);
    },
    messageResent: (props: { mode: AgenticChatMode; model: string }) => {
      void sendAppEvent("Agentic Chat - Message Resent", props);
    },
    newChat: (source: "button" | "conversation_starter") => {
      void sendAppEvent("Agentic Chat - New Chat", { source });
    },
    modeSwitch: (fromMode: AgenticChatMode, toMode: AgenticChatMode) => {
      void sendAppEvent("Agentic Chat - Mode Switch", { fromMode, toMode });
    },
    conversationStarter: (starterLabel: string, requiresWriteMode: boolean, isNewProject: boolean) => {
      void sendAppEvent("Agentic Chat - Conversation Starter", { starterLabel, requiresWriteMode, isNewProject });
    },
    copyMessage: (source: "user" | "assistant", messageLength: number) => {
      void sendAppEvent("Agentic Chat - Copy Message", { source, messageLength });
    },
    revert: (messagesReverted: number, hadOutlineChanges: boolean) => {
      void sendAppEvent("Agentic Chat - Revert", { messagesReverted, hadOutlineChanges });
    },
    acceptAllChanges: (outlineUpdatesCount: number) => {
      void sendAppEvent("Agentic Chat - Accept All Changes", { outlineUpdatesCount });
    },
    feedback: (feedbackType: AgenticChatFeedback, invocationId: string) => {
      void sendAppEvent("Agentic Chat - Feedback", { feedbackType, invocationId });
    },
    selectChat: (chatId: string, messageCount: number) => {
      void sendAppEvent("Agentic Chat - Select Chat", { chatId, messageCount });
    },
    deleteChat: (chatId: string, messageCount: number) => {
      void sendAppEvent("Agentic Chat - Delete Chat", { chatId, messageCount });
    },
    renameChat: (chatId: string) => {
      void sendAppEvent("Agentic Chat - Rename Chat", { chatId });
    },
    copyChat: (chatId: string, messageCount: number) => {
      void sendAppEvent("Agentic Chat - Copy Chat", { chatId, messageCount });
    },
    mentionUsed: (mentionType: AgenticChatMentionType) => {
      void sendAppEvent("Agentic Chat - Mention Used", { mentionType });
    },
    pasteAttachment: (wordCount: number) => {
      void sendAppEvent("Agentic Chat - Paste Attachment", { wordCount });
    },
    importFeedbackShown: (props: ImportFeedbackTrackingProps) => {
      void sendAppEvent("import_feedback_component_shown", props);
    },
    importFeedbackSkipped: (props: ImportFeedbackTrackingProps) => {
      void sendAppEvent("import_feedback_skipped", props);
    },
    importFeedbackSubmitted: (props: ImportFeedbackTrackingProps & { stars: number; hasComment: boolean }) => {
      void sendAppEvent("import_feedback_submitted", props);
    },
    planTryAgainClicked: (props: {
      planId: string;
      planStatus: "completed" | "cancelled";
      stepCount: number;
      completedStepCount: number;
    }) => {
      void sendAppEvent("Agentic Chat - Plan Try Again", props);
    },
    /** Chat Personalization dialog (memory lives under Advanced). */
    chatPersonalizationOpened: (
      source: "navigation" | "memory_preserved_thinking_link" | "language_preference_toast" | "conversation_starter",
    ) => {
      void sendAppEvent("Agentic Chat - Chat Personalization Opened", { source });
    },
    languagePreferenceToastShown: (props: { language_code: string; language_name: string }) => {
      void sendAppEvent("Agentic Chat - Language Preference Toast Shown", props);
    },
    languagePreferenceToastDismissed: (props: { language_code: string; surface: "no_button" | "toast_close" }) => {
      void sendAppEvent("Agentic Chat - Language Preference Toast Dismissed", props);
    },
    languagePreferenceSaveClicked: (props: { language_code: string; language_name: string; had_existing_preference: boolean }) => {
      void sendAppEvent("Agentic Chat - Language Preference Save Clicked", props);
    },
    languagePreferenceSaveSucceeded: (props: { language_code: string }) => {
      void sendAppEvent("Agentic Chat - Language Preference Save Succeeded", props);
    },
    languagePreferenceSaveFailed: (props: { language_code: string }) => {
      void sendAppEvent("Agentic Chat - Language Preference Save Failed", props);
    },
    languagePreferenceOpenPreferencesClicked: () => {
      void sendAppEvent("Agentic Chat - Language Preference Open Preferences Clicked", {});
    },
  },
  characterImage: {
    dialogOpened: (props: { characterId: string; projectId: string; entrySource: EntityImageEntrySource; initialTab: EntityImageTab; hasExistingCover: boolean }) => {
      void sendAppEvent("Character Image - Dialog Opened", props);
    },
    generateClicked: (props: { characterId: string; styleCount: number; isFreeformStyle: boolean; isRegenerate: boolean; hasReferenceImage: boolean; descriptionLength: number; hasPhysicalDescription: boolean }) => {
      void sendAppEvent("Character Image - Generate Clicked", props);
    },
    tileResult: (props: { characterId: string; stylePresetId: string; isFreeformStyle: boolean; isRetry: boolean; hasReferenceImage: boolean; outcome: "success" | "error" | "success_unsaved" }) => {
      void sendAppEvent("Character Image - Tile Result", props);
    },
    retryTileClicked: (props: { characterId: string; stylePresetId: string; isFreeformStyle: boolean; hasReferenceImage: boolean }) => {
      void sendAppEvent("Character Image - Retry Tile Clicked", props);
    },
    uploadFilesAdded: (props: { characterId: string; addedCount: number; totalPendingCount: number; rejectedDueToLimit: boolean }) => {
      void sendAppEvent("Character Image - Upload Files Added", props);
    },
    uploadSaveClicked: (props: { characterId: string; fileCount: number }) => {
      void sendAppEvent("Character Image - Upload Save Clicked", props);
    },
    saveBatchCompleted: (props: { characterId: string; source: "upload"; itemCount: number; succeededCount: number; failedCount: number; becameCover: boolean }) => {
      void sendAppEvent("Character Image - Save Batch Completed", props);
    },
    deleted: (props: { characterId: string; imageId: string; wasCover: boolean }) => {
      void sendAppEvent("Character Image - Deleted", props);
    },
    coverSet: (props: { characterId: string; imageId: string }) => {
      void sendAppEvent("Character Image - Cover Set", props);
    },
  },
  worldbuildingImage: {
    dialogOpened: (props: { worldbuildingEntryId: string; projectId: string; entrySource: EntityImageEntrySource; initialTab: EntityImageTab; hasExistingCover: boolean }) => {
      void sendAppEvent("Worldbuilding Image - Dialog Opened", props);
    },
    generateClicked: (props: { worldbuildingEntryId: string; styleCount: number; isFreeformStyle: boolean; isRegenerate: boolean; hasReferenceImage: boolean; descriptionLength: number; aspectRatio: string | null }) => {
      void sendAppEvent("Worldbuilding Image - Generate Clicked", props);
    },
    tileResult: (props: { worldbuildingEntryId: string; stylePresetId: string; isFreeformStyle: boolean; isRetry: boolean; hasReferenceImage: boolean; outcome: "success" | "error" | "success_unsaved" }) => {
      void sendAppEvent("Worldbuilding Image - Tile Result", props);
    },
    retryTileClicked: (props: { worldbuildingEntryId: string; stylePresetId: string; isFreeformStyle: boolean; hasReferenceImage: boolean }) => {
      void sendAppEvent("Worldbuilding Image - Retry Tile Clicked", props);
    },
    uploadFilesAdded: (props: { worldbuildingEntryId: string; addedCount: number; totalPendingCount: number; rejectedDueToLimit: boolean }) => {
      void sendAppEvent("Worldbuilding Image - Upload Files Added", props);
    },
    uploadSaveClicked: (props: { worldbuildingEntryId: string; fileCount: number }) => {
      void sendAppEvent("Worldbuilding Image - Upload Save Clicked", props);
    },
    saveBatchCompleted: (props: { worldbuildingEntryId: string; source: "upload"; itemCount: number; succeededCount: number; failedCount: number; becameCover: boolean }) => {
      void sendAppEvent("Worldbuilding Image - Save Batch Completed", props);
    },
    deleted: (props: { worldbuildingEntryId: string; imageId: string; wasCover: boolean }) => {
      void sendAppEvent("Worldbuilding Image - Deleted", props);
    },
    coverSet: (props: { worldbuildingEntryId: string; imageId: string }) => {
      void sendAppEvent("Worldbuilding Image - Cover Set", props);
    },
  },
};
